import { MdCancel } from "react-icons/md";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { MdAttachFile } from "react-icons/md";
import { BsGrid3X3Gap } from "react-icons/bs";
import { FaList } from "react-icons/fa";
import { TbMailShare } from "react-icons/tb";
import { useEffect, useState } from "react";
import { S3Client, GetObjectCommand, ListObjectsV2Command } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import * as pdfjsLib from "pdfjs-dist";
import { getStatusClass } from "./AgencyDetailPageLogic";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const MailHistoryViewSwitch = ({ className = "", mailViewMode, setMailViewMode }) => (
  <div
    className={`adp-view-toggle-btns ${className}`.trim()}
    role="group"
    aria-label="Mail history view"
    onClick={(event) => event.stopPropagation()}
    onKeyDown={(event) => event.stopPropagation()}
  >
    <button
      className={`adp-view-toggle-btn ${mailViewMode === "grid" ? "active" : ""}`}
      type="button"
      onClick={() => setMailViewMode("grid")}
      title="Grid View"
      aria-label="Grid View"
    >
      <BsGrid3X3Gap />
    </button>

    <button
      className={`adp-view-toggle-btn ${mailViewMode === "list" ? "active" : ""}`}
      type="button"
      onClick={() => setMailViewMode("list")}
      title="List View"
      aria-label="List View"
    >
      <FaList />
    </button>
  </div>
);

export const MailHistorySectionContent = ({ records, mailViewMode, setSelectedMail, datasetID, datasetName }) => {
  const [attachmentNamesByMailId, setAttachmentNamesByMailId] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadAttachmentNames = async () => {
      const nextAttachmentNames = {};

      await Promise.all(records.map(async (record, index) => {
        const mailItem = record.raw || {};
        if (!hasMailAttachment(mailItem)) return;

        const recordKey = getMailRecordKey(record, index);
        const localNames = getAttachmentNamesFromMailItem(mailItem);
        if (localNames.length > 0) {
          nextAttachmentNames[recordKey] = localNames;
          return;
        }

        try {
          const s3 = getAuthenticatedS3Client();
          const bucketName = getMailAttachmentBucketName(mailItem, datasetID, datasetName);
          if (!bucketName) {
            nextAttachmentNames[recordKey] = ["Attachment"];
            return;
          }

          const keys = await resolveAttachmentKeys(mailItem, s3, bucketName);
          const names = keys.map(getFileNameFromKey).filter(Boolean);
          nextAttachmentNames[recordKey] = names.length > 0 ? names : ["Attachment"];
        } catch (error) {
          console.warn("Unable to resolve agency mail attachment names", error);
          nextAttachmentNames[recordKey] = ["Attachment"];
        }
      }));

      if (!cancelled) {
        setAttachmentNamesByMailId(nextAttachmentNames);
      }
    };

    loadAttachmentNames();

    return () => {
      cancelled = true;
    };
  }, [records, datasetID, datasetName]);

  const handleAttachmentClick = async (event, mailItem, fileName, fileIndex) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const s3 = getAuthenticatedS3Client();
      const bucketName = getMailAttachmentBucketName(mailItem, datasetID, datasetName);
      if (!bucketName) return;

      const keys = await resolveAttachmentKeys(mailItem, s3, bucketName);
      const matchingKey = keys.find((key) => getFileNameFromKey(key) === fileName);
      const selectedKey = matchingKey || keys[fileIndex] || keys[0];

      if (!selectedKey) return;

      const command = new GetObjectCommand({ Bucket: bucketName, Key: selectedKey });
      const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.warn("Unable to open agency mail attachment", error);
    }
  };

  const renderAttachmentChips = (record, index, className = "") => {
    const mailItem = record.raw || {};
    if (!hasMailAttachment(mailItem)) return null;

    const recordKey = getMailRecordKey(record, index);
    const attachmentNames = attachmentNamesByMailId[recordKey] || getAttachmentNamesFromMailItem(mailItem) || ["Attachment"];
    const visibleAttachmentNames = attachmentNames.slice(0, 3);
    const hiddenAttachmentCount = Math.max(attachmentNames.length - 3, 0);

    return (
      <div className={`adp-mail-item-attachments-row ${className}`.trim()}>
        {visibleAttachmentNames.map((fileName, attachmentIndex) => (
          <button
            type="button"
            className="mail-item-attachment-chip"
            key={`${recordKey}-att-${attachmentIndex}`}
            onClick={(event) => handleAttachmentClick(event, mailItem, fileName, attachmentIndex)}
            title={fileName}
          >
            <span className="mail-item-attachment-type">{getAttachmentTypeLabel(fileName)}</span>
            <span className="mail-item-attachment-name">{fileName}</span>
          </button>
        ))}
        {hiddenAttachmentCount > 0 && (
          <span className="mail-item-attachment-more" title={`${hiddenAttachmentCount} more attachments`}>
            +{hiddenAttachmentCount}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="adp-email-history-body">
      <div className={mailViewMode === "grid" ? "adp-mail-history-grid" : "adp-mail-history-list"}>
        {records.map((record, index) => (
          mailViewMode === "grid" ? (
            <div
              className="adp-mail-history-item-grid"
              key={record.id || `mail-grid-${index}`}
              onClick={() => setSelectedMail(record.raw)}
              style={{ cursor: "pointer" }}
            >
              <div className="adp-mail-item-content-grid">
                <div className="adp-mail-item-top-grid">
                  <span className="adp-mail-item-subject-grid">{record.subject}</span>
                </div>
                <div className="adp-mail-item-to-grid">To: {record.recipients}</div>
                <div className="adp-mail-item-date-grid">
                  <span className={`adp-mail-item-date-label-grid ${getStatusClass(record.status)}`}>
                    {record.status}:
                  </span>
                  {record.date}
                </div>
                <div className="adp-mail-item-vin-grid">{record.vinId || "NA"}</div>
                {/* {renderAttachmentChips(record, index, "adp-mail-item-attachments-row-grid")} */}
              </div>
            </div>
          ) : (
            <div
              className="adp-mail-history-item"
              key={record.id || `mail-list-${index}`}
              onClick={() => setSelectedMail(record.raw)}
              style={{ cursor: "pointer" }}
            >
              <div className="adp-mail-item-vin-list">{record.vinId || "NA"}</div>
              <div className="adp-mail-share-toggle">
                <TbMailShare />
              </div>
              <div className="adp-mail-item-content">
                <div className="adp-mail-item-to">To: {record.recipients}</div>
                <div className="adp-mail-item-subject">{record.subject}</div>
                <div className="adp-mail-item-snippet">
                  Mail status is {record.status} for this agency request.
                </div>
                {renderAttachmentChips(record, index)}
              </div>
              <div className="adp-mail-item-meta">
                <div className="adp-mail-item-date">
                  <span className={`adp-mail-item-status ${getStatusClass(record.status)}`}>
                    {record.status}
                  </span>
                  <span>{record.date}</span>
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

const normalizeBucketNamePart = (value) => (
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
);

const getAuthenticatedS3Client = () => {
  const idToken = localStorage.getItem("idToken");

  return new S3Client({
    region: process.env.REACT_APP_AWS_REGION,
    credentials: fromCognitoIdentityPool({
      clientConfig: { region: process.env.REACT_APP_AWS_REGION },
      identityPoolId: process.env.REACT_APP_CONNECT_IDENTITY_POOL_ID,
      logins: {
        [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_COGNITO_USERPOOLID}`]: idToken,
      },
    }),
  });
};

const getMailAttachmentBucketName = (mailItem = {}, datasetID = "", datasetName = "") => {
  const datasetIdPart = normalizeBucketNamePart(datasetID || mailItem.datasetID || mailItem.dataSetId);
  const datasetNamePart = normalizeBucketNamePart(datasetName || mailItem.datasetName || mailItem.dataSetName);
  const aliasPart = normalizeBucketNamePart(process.env.REACT_APP_ALIAS);

  if (datasetIdPart && datasetNamePart && aliasPart) {
    return `${datasetIdPart}-${datasetNamePart}-files-${aliasPart}`;
  }

  return "";
};

const readAttachmentKeys = (mailItem = {}) => {
  const explicitKeys = [
    ...(Array.isArray(mailItem.s3Keys) ? mailItem.s3Keys : []),
    ...(Array.isArray(mailItem.attachmentKeys) ? mailItem.attachmentKeys : []),
    ...(Array.isArray(mailItem.attachments)
      ? mailItem.attachments.map((attachment) => attachment?.s3Key || attachment?.key)
      : []),
  ].filter(Boolean);

  return [...new Set(explicitKeys.map((key) => String(key).trim()).filter(Boolean))];
};

const hasMailAttachment = (mailItem = {}) => (
  Boolean(mailItem.hasAttachment || mailItem.hasAttachments || readAttachmentKeys(mailItem).length > 0)
);

const getFileNameFromKey = (key) => String(key || "").split("/").pop() || "";

const getMailRecordKey = (record, index) => record.id || record.raw?._id || record.raw?.id || `mail-${index}`;

const getAttachmentNamesFromMailItem = (mailItem = {}) => {
  const attachmentNames = [
    ...(Array.isArray(mailItem.attachmentNames) ? mailItem.attachmentNames : []),
    ...(Array.isArray(mailItem.attachments)
      ? mailItem.attachments.map((attachment) => attachment?.fileName || attachment?.name || getFileNameFromKey(attachment?.s3Key || attachment?.key))
      : []),
    ...readAttachmentKeys(mailItem).map(getFileNameFromKey),
  ].filter(Boolean);

  if (attachmentNames.length > 0) {
    return [...new Set(attachmentNames.map((name) => String(name).trim()).filter(Boolean))];
  }

  const rootKey = String(mailItem.s3Key || "").trim();
  if (rootKey.includes(".") && !rootKey.endsWith("/")) return [getFileNameFromKey(rootKey)];

  return [];
};

const resolveAttachmentKeys = async (mailItem, s3, bucketName) => {
  if (!hasMailAttachment(mailItem)) return [];

  const explicitKeys = readAttachmentKeys(mailItem);
  if (explicitKeys.length > 0) return explicitKeys;

  const rootKey = String(mailItem.s3Key || "").trim();
  if (!rootKey) return [];

  if (rootKey.includes(".") && !rootKey.endsWith("/")) return [rootKey];

  const prefix = rootKey.endsWith("/") ? rootKey : `${rootKey}/`;
  const listCommand = new ListObjectsV2Command({
    Bucket: bucketName,
    Prefix: prefix,
  });
  const listResponse = await s3.send(listCommand);

  const keys = (listResponse.Contents || [])
    .map((obj) => obj.Key)
    .filter((key) => key && !key.endsWith("/"));

  return keys.length > 0 ? keys : [rootKey];
};

const getAttachmentTypeLabel = (name) => {
  if (!name || !name.includes(".")) return "FILE";
  const ext = name.split(".").pop()?.trim().toUpperCase();
  if (!ext) return "FILE";
  return ext.length > 5 ? ext.slice(0, 5) : ext;
};

const isImageAttachment = (name) => {
  const ext = (name?.split(".").pop() || "").toLowerCase();
  return ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
};

const isPdfAttachment = (name) => {
  const ext = (name?.split(".").pop() || "").toLowerCase();
  return ext === "pdf";
};

const isTextPreviewAttachment = (name) => {
  const ext = (name?.split(".").pop() || "").toLowerCase();
  return ["txt", "csv", "json", "xml", "log"].includes(ext);
};

const createPdfThumbnail = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error("Unable to fetch PDF preview");
  }

  const pdfBytes = await response.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
  const pdfDoc = await loadingTask.promise;
  try {
    const page = await pdfDoc.getPage(1);

    const viewport = page.getViewport({ scale: 1 });
    const targetWidth = 220;
    const scale = targetWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Unable to create PDF preview canvas");
    }

    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;

    await page.render({
      canvasContext: context,
      viewport: scaledViewport,
    }).promise;

    return canvas.toDataURL("image/jpeg", 0.9);
  } finally {
    await pdfDoc.destroy();
  }
};

export const MailOpenModal = ({
  selectedMail,
  setSelectedMail,
  showMailHeaderDetails,
  setShowMailHeaderDetails,
  mailHeaderToggleRef,
  mailHeaderDetailsRef,
  getSenderInitial,
  getSenderDisplayName,
  formatMailDateTime,
  datasetID,
  datasetName,
}) => {
  const [mailAttachments, setMailAttachments] = useState([]);
  const [pdfThumbnails, setPdfThumbnails] = useState({});

  useEffect(() => {
    if (!selectedMail) {
      setMailAttachments([]);
      setPdfThumbnails({});
      return;
    }

    let cancelled = false;

    const loadAttachments = async () => {
      if (!hasMailAttachment(selectedMail)) {
        setMailAttachments([]);
        setPdfThumbnails({});
        return;
      }

      try {
        const s3 = getAuthenticatedS3Client();
        const bucketName = getMailAttachmentBucketName(selectedMail, datasetID, datasetName);
        if (!bucketName) {
          setMailAttachments([]);
          setPdfThumbnails({});
          return;
        }

        const objectKeys = await resolveAttachmentKeys(selectedMail, s3, bucketName);

        const attachments = await Promise.all(
          objectKeys.map(async (key) => {
            const command = new GetObjectCommand({ Bucket: bucketName, Key: key });
            const url = await getSignedUrl(s3, command, { expiresIn: 3600 });

            return {
              key,
              name: key.split("/").pop() || "attachment",
              url,
            };
          })
        );

        if (!cancelled) {
          setMailAttachments(attachments);
          setPdfThumbnails({});
        }
      } catch (error) {
        console.warn("Unable to load agency mail attachments", error);
        if (!cancelled) {
          setMailAttachments([]);
          setPdfThumbnails({});
        }
      }
    };

    loadAttachments();

    return () => {
      cancelled = true;
    };
  }, [selectedMail, datasetID, datasetName]);

  useEffect(() => {
    if (!selectedMail || mailAttachments.length === 0) return;
    let cancelled = false;

    const generateThumbnails = async () => {
      const pdfItems = mailAttachments.filter((attachment) => isPdfAttachment(attachment.name));
      if (pdfItems.length === 0) return;

      const thumbnails = await Promise.all(
        pdfItems.map(async (attachment) => {
          try {
            const thumb = await createPdfThumbnail(attachment.url);
            return [attachment.key, thumb];
          } catch (error) {
            console.warn("Unable to generate agency mail PDF thumbnail", attachment.name, error);
            return null;
          }
        })
      );

      const validThumbnails = thumbnails.filter(Boolean);
      if (!cancelled && validThumbnails.length > 0) {
        setPdfThumbnails((previous) => {
          const next = { ...previous };
          validThumbnails.forEach(([key, thumb]) => {
            next[key] = thumb;
          });
          return next;
        });
      }
    };

    generateThumbnails();

    return () => {
      cancelled = true;
    };
  }, [selectedMail, mailAttachments]);

  const handleDirectDownload = async (event, url, name) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = name || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.warn("Unable to download agency mail attachment", error);
    }
  };

  if (!selectedMail) return null;

  return (
    <div className="mail-open-overlay" onClick={() => setSelectedMail(null)}>
      <div className="mail-open-modal" onClick={(event) => event.stopPropagation()}>
        <div className="mail-open-toolbar">
          <button type="button" className="mail-tool-icon" onClick={() => setSelectedMail(null)}>
            <MdCancel size={25} />
          </button>
        </div>

        <div className="mail-open-header">
          <h4>{selectedMail.subject || "No Subject"}</h4>
        </div>

        <div className="mail-open-meta">
          <div className="mail-open-avatar">{getSenderInitial(selectedMail)}</div>
          <div className="mail-open-meta-content">
            <div className="mail-open-from-row">
              <strong>{getSenderDisplayName(selectedMail)}</strong>
              <span className="mail-open-from-address">&lt;{selectedMail.from || "NA"}&gt;</span>
              <span className="mail-open-time">{formatMailDateTime(selectedMail.sentAt || selectedMail.scheduleTime)}</span>
            </div>
            <button
              type="button"
              className="mail-open-to-toggle"
              ref={mailHeaderToggleRef}
              onClick={() => setShowMailHeaderDetails((previous) => !previous)}
              aria-expanded={showMailHeaderDetails}
            >
              <span className="mail-open-to-row">
                to {selectedMail.recipients?.join(", ") || "NA"}
              </span>
              {showMailHeaderDetails ? <IoIosArrowUp size={16} /> : <IoIosArrowDown size={16} />}
            </button>

            {showMailHeaderDetails && (
              <div className="mail-open-detail-card" ref={mailHeaderDetailsRef}>
                <div className="mail-open-detail-row">
                  <span className="mail-open-detail-label">from:</span>
                  <span className="mail-open-detail-value">
                    <strong>{getSenderDisplayName(selectedMail)}</strong>
                    <span className="mail-open-detail-address">&lt;{selectedMail.from || "NA"}&gt;</span>
                  </span>
                </div>
                <div className="mail-open-detail-row">
                  <span className="mail-open-detail-label">to:</span>
                  <span className="mail-open-detail-value">
                    {selectedMail.recipients?.length > 0
                      ? selectedMail.recipients.map((mail, index) => (
                        <div key={index}>{mail}{index !== selectedMail.recipients.length - 1 && ","}</div>
                      ))
                      : "NA"}
                  </span>
                </div>
                {selectedMail.ccrecipients?.length > 0 && (
                  <div className="mail-open-detail-row">
                    <span className="mail-open-detail-label">cc:</span>
                    <span className="mail-open-detail-value">
                      {selectedMail.ccrecipients.map((mail, index) => (
                        <div key={index}>{mail}{index !== selectedMail.ccrecipients.length - 1 && ","}</div>
                      ))}
                    </span>
                  </div>
                )}
                {selectedMail.bccrecipients?.length > 0 && (
                  <div className="mail-open-detail-row">
                    <span className="mail-open-detail-label">bcc:</span>
                    <span className="mail-open-detail-value">
                      {selectedMail.bccrecipients.map((mail, index) => (
                        <div key={index}>{mail}{index !== selectedMail.bccrecipients.length - 1 && ","}</div>
                      ))}
                    </span>
                  </div>
                )}
                <div className="mail-open-detail-row">
                  <span className="mail-open-detail-label">date:</span>
                  <span className="mail-open-detail-value">
                    {formatMailDateTime(selectedMail.sentAt || selectedMail.scheduleTime)}
                  </span>
                </div>
                <div className="mail-open-detail-row">
                  <span className="mail-open-detail-label">subject:</span>
                  <span className="mail-open-detail-value">{selectedMail.subject || "No Subject"}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mail-open-body">
          {selectedMail.html || selectedMail.body || selectedMail.message
            ? /<\/?[a-z][\s\S]*>/i.test(selectedMail.html || "")
              ? <div dangerouslySetInnerHTML={{ __html: selectedMail.html }} />
              : (selectedMail.html || selectedMail.body || selectedMail.message)
            : "No message content available."}
        </div>

        {mailAttachments.length > 0 && (
          <div className="mail-attachment-wrap">
            <div className="mail-attachment-title">
              <span>{mailAttachments.length} Attachment{mailAttachments.length > 1 ? "s" : ""}</span>
            </div>

            <div className="mail-attachment-list">
              {mailAttachments.map((attachment) => (
                <a
                  key={attachment.key}
                  className="mail-attachment-card"
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={attachment.name}
                >
                  <div className="mail-attachment-preview">
                    {isImageAttachment(attachment.name) ? (
                      <img
                        src={attachment.url}
                        alt={attachment.name}
                        className="mail-attachment-thumb"
                      />
                    ) : isPdfAttachment(attachment.name) ? (
                      <div className="mail-attachment-pdf-preview">
                        {pdfThumbnails[attachment.key] ? (
                          <img
                            src={pdfThumbnails[attachment.key]}
                            alt={attachment.name}
                            className="mail-attachment-thumb"
                          />
                        ) : (
                          <MdAttachFile size={20} />
                        )}
                      </div>
                    ) : isTextPreviewAttachment(attachment.name) ? (
                      <iframe
                        src={attachment.url}
                        title={attachment.name}
                        className="mail-attachment-text-preview"
                        sandbox=""
                      />
                    ) : (
                      <MdAttachFile size={22} />
                    )}

                    <div className="mail-attachment-hover-actions">
                      <button
                        type="button"
                        className="mail-attachment-action-btn"
                        title="Download"
                        onClick={(event) => handleDirectDownload(event, attachment.url, attachment.name)}
                      >
                        <FiDownload size={14} />
                      </button>
                      <button
                        type="button"
                        className="mail-attachment-action-btn"
                        title="Open"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          window.open(attachment.url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <FiExternalLink size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="mail-attachment-footer">
                    <span className="mail-attachment-ext">{getAttachmentTypeLabel(attachment.name)}</span>
                    <span className="mail-attachment-file">{attachment.name}</span>
                    <span className="mail-attachment-corner" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mail-open-actions" />
      </div>
    </div>
  );
};

const MailHistoryModal = ({ isOpen, onClose, headerActions, children }) => {
  if (!isOpen) return null;

  return (
    <div className="adp-section-modal-overlay" >
      <div className="adp-section-modal" >
        <div className="adp-section-modal-header">
          <span>Mail History</span>
          <div className="adp-section-modal-header-actions">
            {headerActions}
            <button className="adp-section-modal-close" type="button" onClick={onClose} aria-label="Close Mail History">
              <MdCancel size={25} />
            </button>
          </div>
        </div>
        <div className="adp-section-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default MailHistoryModal;
